/**
 * Scenario template position + combo metadata.
 *
 * Ported from kula-create `lib/templates/templateRegistry.ts`. KulaLearn
 * keeps the same template ids — the position / combo data tells the
 * compositor which sequences make pedagogical sense.
 *
 * - `position`: where in a 2–3 step sequence the template is most natural.
 *   - opener: introduces a topic, surfaces priors, low-stakes.
 *   - core: the substantive interaction.
 *   - closer: synthesis, commitment, transfer.
 * - `combosWellWith`: explicit "good pairing" hints (warning-only when missed).
 * - `combosAvoid`: hard "do not pair" — rejects sequence validation.
 */

export type ScenarioTemplateId =
  | 'reveal-predict'
  | 'scenario-simulation'
  | 'spot-the-error'
  | 'drag-sort'
  | 'gallery-walk'
  | 'timeline-navigator'
  | 'confidence-meter'
  | 'progressive-builder'
  | 'guided-sandbox'
  | 'scaffolded-investigation'
  | 'perspective-lenses'
  | 'tension-mapper'
  | 'before-after-bridge'
  | 'claim-evidence'
  | 'layered-reveal'
  | 'reflect-act'
  | 'teach-back';

export type ScenarioPosition = 'opener' | 'core' | 'closer';

export interface ScenarioTemplateMetadata {
  id: ScenarioTemplateId;
  label: string;
  position: ScenarioPosition;
  combosWellWith: ScenarioTemplateId[];
  combosAvoid: ScenarioTemplateId[];
}

export const SCENARIO_TEMPLATE_REGISTRY: Record<ScenarioTemplateId, ScenarioTemplateMetadata> = {
  'reveal-predict': {
    id: 'reveal-predict',
    label: 'Reveal & Predict',
    position: 'opener',
    combosWellWith: ['perspective-lenses'],
    combosAvoid: [],
  },
  'scenario-simulation': {
    id: 'scenario-simulation',
    label: 'Scenario Simulation',
    position: 'core',
    combosWellWith: ['reflect-act'],
    combosAvoid: [],
  },
  'spot-the-error': {
    id: 'spot-the-error',
    label: 'Spot the Error',
    position: 'core',
    combosWellWith: ['perspective-lenses'],
    combosAvoid: ['claim-evidence'],
  },
  'drag-sort': {
    id: 'drag-sort',
    label: 'Drag & Sort',
    position: 'core',
    combosWellWith: ['claim-evidence'],
    combosAvoid: [],
  },
  'teach-back': {
    id: 'teach-back',
    label: 'Teach-Back',
    position: 'core',
    // Teaching is a strong debrief lead-in; pairs after a scenario the learner lived.
    combosWellWith: ['reflect-act', 'scenario-simulation'],
    combosAvoid: [],
  },
  'gallery-walk': {
    id: 'gallery-walk',
    label: 'Gallery Walk',
    position: 'core',
    combosWellWith: [],
    combosAvoid: ['gallery-walk'],
  },
  'timeline-navigator': {
    id: 'timeline-navigator',
    label: 'Timeline Navigator',
    position: 'core',
    combosWellWith: [],
    combosAvoid: [],
  },
  'confidence-meter': {
    id: 'confidence-meter',
    label: 'Confidence Meter',
    position: 'opener',
    combosWellWith: ['before-after-bridge'],
    combosAvoid: ['confidence-meter'],
  },
  'progressive-builder': {
    id: 'progressive-builder',
    label: 'Progressive Builder',
    position: 'core',
    combosWellWith: [],
    combosAvoid: [],
  },
  'guided-sandbox': {
    id: 'guided-sandbox',
    label: 'Guided Sandbox',
    position: 'closer',
    combosWellWith: [],
    combosAvoid: [],
  },
  'scaffolded-investigation': {
    id: 'scaffolded-investigation',
    label: 'Scaffolded Investigation',
    position: 'core',
    combosWellWith: [],
    combosAvoid: [],
  },
  'perspective-lenses': {
    id: 'perspective-lenses',
    label: 'Perspective Lenses',
    position: 'core',
    combosWellWith: ['reveal-predict', 'spot-the-error', 'layered-reveal'],
    combosAvoid: ['tension-mapper'],
  },
  'tension-mapper': {
    id: 'tension-mapper',
    label: 'Tension Mapper',
    position: 'core',
    combosWellWith: ['reflect-act'],
    combosAvoid: ['tension-mapper', 'perspective-lenses'],
  },
  'before-after-bridge': {
    id: 'before-after-bridge',
    label: 'Before & After Bridge',
    position: 'opener',
    combosWellWith: ['confidence-meter', 'reflect-act'],
    combosAvoid: ['layered-reveal'],
  },
  'claim-evidence': {
    id: 'claim-evidence',
    label: 'Claim & Evidence',
    position: 'core',
    combosWellWith: ['layered-reveal', 'drag-sort'],
    combosAvoid: ['spot-the-error'],
  },
  'layered-reveal': {
    id: 'layered-reveal',
    label: 'Layered Reveal',
    position: 'core',
    combosWellWith: ['claim-evidence', 'perspective-lenses'],
    combosAvoid: ['before-after-bridge'],
  },
  'reflect-act': {
    id: 'reflect-act',
    label: 'Reflect & Act',
    position: 'closer',
    combosWellWith: ['scenario-simulation', 'tension-mapper', 'before-after-bridge'],
    combosAvoid: ['reflect-act'],
  },
};

export function getScenarioTemplateMetadata(id: string): ScenarioTemplateMetadata | undefined {
  return SCENARIO_TEMPLATE_REGISTRY[id as ScenarioTemplateId];
}

export function isScenarioTemplateId(id: string): id is ScenarioTemplateId {
  return id in SCENARIO_TEMPLATE_REGISTRY;
}
